import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Offer as OfferType, Product } from 'queries';
import { Colors } from '@app/theme';
import { TextAndImageSelectionField } from '@app/form';
import { useSupportOfferIllustration } from './utils/useSupportOfferIllustration';
import { getSimpleOfferPrice } from 'lib/priceComputation';
import { filterAlreadySelected } from 'lib/planAndOffer';

interface Props {
  offer: OfferType;
  name: string;
  alreadySelectedProducts?: Product[];
  className?: string;
  isCompact?: boolean;
  noIllustration?: boolean;
  onClick?: (offer: OfferType) => void;
  disabled?: boolean;
  trackingName?: string;
}

export const SupportOfferBlock = ({
  offer,
  name,
  className,
  alreadySelectedProducts = [],
  isCompact,
  noIllustration,
  onClick,
  disabled,
  trackingName,
}: Props) => {
  const { t } = useTranslation();
  const { amount: total } = useMemo(
    () =>
      getSimpleOfferPrice(
        filterAlreadySelected(offer.products, alreadySelectedProducts),
      ),
    [offer.products, alreadySelectedProducts],
  );
  const illustration = useSupportOfferIllustration(offer);

  let chip: { label: string; color: Colors | undefined } | undefined;

  if (total !== 0 || noIllustration) {
    chip = {
      label: t('studySetup.generalSetup.support.price', {
        count: total,
      }),
      color: 'accent',
    };
  }
  if (total === 0 && illustration) {
    chip = {
      label: t('studySetup.generalSetup.support.price', {
        count: total,
      }),
      color: 'inkSecondary',
    };
  }
  if (offer.requireCallToSales) {
    chip = {
      label: t('studySetup.generalSetup.support.startingAt', {
        count: total,
      }),
      color: 'accent',
    };
  }

  const onChange = useCallback(() => {
    onClick && onClick(offer);
  }, [offer, onClick]);

  return (
    <TextAndImageSelectionField
      illustration={noIllustration ? undefined : illustration}
      disabled={disabled}
      title={offer.name}
      description={offer.description}
      className={className}
      name={name}
      value={offer.id}
      centerChip={chip}
      isCompact={isCompact}
      onChange={onChange}
      trackingName={trackingName}
    />
  );
};
